import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  TextField,
  MenuItem,
  Button,
  Divider,
} from '@mui/material'
import FilterListIcon from '@mui/icons-material/FilterList'
import DashboardSearchableSelect from './DashboardSearchableSelect'
import { DASHBOARD_COLORS, dashboardCardSx } from './dashboardFormatters'
import { useEffect, useMemo, useState } from 'react'
import { joinDashboardIds, parseDashboardIdList } from '../../hooks/useDashboard'

import { PERIOD_OPTIONS } from '../../hooks/dashboardPeriod'

const dateFieldSx = {
  width: 168,
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    bgcolor: '#ecece8',
    fontSize: '13px',
    color: '#6b6b66',
    '& fieldset': {
      borderColor: 'transparent',
    },
    '&:hover fieldset': {
      borderColor: 'rgba(0,0,0,0.08)',
    },
    '&.Mui-focused fieldset': {
      borderColor: DASHBOARD_COLORS.red,
    },
  },
  '& .MuiInputBase-input': {
    py: 1,
    px: 1.5,
  },
}

const DashboardFilterBar = ({
  filters,
  setFilter,
  locations,
  categories,
  partners,
  onLocationSearch,
  onCategorySearch,
  onPartnerSearch,
  locationLoading,
  categoryLoading,
  partnerLoading,
  onGlobalPeriodChange,
  onGlobalCustomRangeApply,
}) => {
  const [customStart, setCustomStart] = useState(filters.globalStart || '')
  const [customEnd, setCustomEnd] = useState(filters.globalEnd || '')
  useEffect(() => {
    setCustomStart(filters.globalStart || '')
  }, [filters.globalStart])
  useEffect(() => {
    setCustomEnd(filters.globalEnd || '')
  }, [filters.globalEnd])

  const activeCount = useMemo(() => {
    let n = 0
    if (parseDashboardIdList(filters.locationIds).length) n += 1
    if (parseDashboardIdList(filters.categoryIds).length) n += 1
    if (parseDashboardIdList(filters.organizationIds).length) n += 1
    return n
  }, [filters.locationIds, filters.categoryIds, filters.organizationIds])

  const isCustom = filters.globalPeriod === 'custom'
  const canApply =
    Boolean(customStart) && Boolean(customEnd) && customStart <= customEnd

  return (
    <Paper
      elevation={0}
      sx={{
        ...dashboardCardSx,
        px: 2,
        py: 1.25,
        mb: 2,
        borderRadius: 0,
        borderLeft: 'none',
        borderRight: 'none',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        boxShadow: 'none',
        borderTop: 'none',
      }}
    >
      <Stack spacing={1.25}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={1.5}
          alignItems={{ md: 'center' }}
          flexWrap="wrap"
        >
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ flexShrink: 0 }}
          >
            <FilterListIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
              }}
            >
              Global Filter
            </Typography>
          </Stack>

          <DashboardSearchableSelect
            label="Location"
            options={locations}
            value={filters.locationIds}
            multiple
            onChange={(opts) => setFilter({ locationIds: joinDashboardIds(opts) })}
            onSearch={onLocationSearch}
            loading={locationLoading}
          />
          <DashboardSearchableSelect
            label="Category"
            options={categories}
            value={filters.categoryIds}
            multiple
            onChange={(opts) => setFilter({ categoryIds: joinDashboardIds(opts) })}
            onSearch={onCategorySearch}
            loading={categoryLoading}
          />

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              display: { xs: 'none', md: 'block' },
              mx: 0.5,
              borderColor: 'rgba(0,0,0,0.12)',
              minHeight: 36,
            }}
          />

          <DashboardSearchableSelect
            label="Partner Shop"
            options={partners}
            value={filters.organizationIds}
            multiple
            onChange={(opts) =>
              setFilter({ organizationIds: joinDashboardIds(opts) })
            }
            onSearch={onPartnerSearch}
            loading={partnerLoading}
          />

          <Divider
            orientation="vertical"
            flexItem
            sx={{
              display: { xs: 'none', md: 'block' },
              mx: 0.5,
              borderColor: 'rgba(0,0,0,0.12)',
              minHeight: 36,
            }}
          />

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="caption"
              sx={{
                fontWeight: 700,
                color: 'text.secondary',
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                flexShrink: 0,
              }}
            >
              Period
            </Typography>
            <TextField
              select
              size="small"
              value={filters.globalPeriod || '7d'}
              onChange={(event) => onGlobalPeriodChange?.(event.target.value)}
              sx={{
                minWidth: 150,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '20px',
                  bgcolor: '#fff',
                  fontSize: '12.5px',
                  fontWeight: 700,
                  '& fieldset': {
                    borderColor: isCustom ? DASHBOARD_COLORS.red : 'rgba(0,0,0,0.13)',
                    borderWidth: isCustom ? '1.5px' : '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: isCustom ? DASHBOARD_COLORS.red : 'rgba(0,0,0,0.23)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: DASHBOARD_COLORS.red,
                  },
                },
              }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {activeCount > 0 && (
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Chip
                size="small"
                sx={{
                  bgcolor: DASHBOARD_COLORS.red,
                  color: '#fff',
                  fontWeight: 700,
                }}
                label="Filtered"
              />
              <Chip
                size="small"
                sx={{
                  bgcolor: DASHBOARD_COLORS.red,
                  color: '#fff',
                  fontWeight: 700,
                  minWidth: 28,
                }}
                label={activeCount}
                aria-label={`${activeCount} active filter${activeCount === 1 ? '' : 's'}`}
              />
            </Stack>
          )}

          <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
        </Stack>

        {isCustom && (
          <Stack
            direction="row"
            spacing={1.25}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
          >
            <TextField
              type="date"
              size="small"
              value={customStart}
              onChange={(event) => setCustomStart(event.target.value)}
              inputProps={{ 'aria-label': 'Start date' }}
              sx={dateFieldSx}
            />
            <Typography
              sx={{
                color: '#8a8a84',
                fontSize: '16px',
                lineHeight: 1,
                px: 0.25,
              }}
            >
              →
            </Typography>
            <TextField
              type="date"
              size="small"
              value={customEnd}
              onChange={(event) => setCustomEnd(event.target.value)}
              inputProps={{ 'aria-label': 'End date' }}
              sx={dateFieldSx}
            />
            <Button
              size="small"
              variant="contained"
              disabled={!canApply}
              onClick={() =>
                onGlobalCustomRangeApply?.({
                  start: customStart,
                  end: customEnd,
                })
              }
              sx={{
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 700,
                px: 2.25,
                minWidth: 78,
                height: 36,
                bgcolor: DASHBOARD_COLORS.red,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#b00218', boxShadow: 'none' },
                '&.Mui-disabled': {
                  bgcolor: '#f0b4bb',
                  color: '#fff',
                },
              }}
            >
              Apply
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  )
}

export default DashboardFilterBar
